import { useState } from "react";
import ICONS from "../../../assets/Icons";
import { MONTHS } from "../../../utils/dateConstants";
import globalText from "../../../utils/globalText";
import { globalTextFormat } from "../../../utils/globalTextFormat";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import FileFormatMapper from "../FileFormatMapper";
import MediumImagePopup from "../MediumImagePopup";
import SVGComponent from "../SVGComponent";

/**
 * @typedef FileOnClickParamType
 * @property {string} name
 * @property {"image" | "file"| "image-url"} type
 *
 * @callback FileOnClickType
 * @param {FileOnClickParamType} p
 * @returns {void}
 */
/**
 * @typedef {Object} AdditionalDetailsPreviewMapperProps
 * @property {Array<Object>} mappingData - List of additional fields with name, value, and data_type
 * @property {Object<string, string>} previewImages - Preloaded image URLs for uploaded files (keyed by field name & type)
 * @property { FileOnClickType} fileOnClick
 */

/**
 * Renders a read-only preview of all dynamic additional details.
 * Normal fields always render (with "Not given" when empty).
 * URL / file / image fields render in an Attachments card, only when they have a value.
 *
 * @param {AdditionalDetailsPreviewMapperProps} props
 */

// Set to true to also show empty URL / file / image fields as "Not given"
const SHOW_EMPTY_ATTACHMENTS = true;

const IMAGE_TYPES = ["PNG", "BMP", "JPEG", "JPG"];
const IMAGE_EXTENSIONS = ["png", "bmp", "jpeg", "jpg"];

const tileClass =
  "flex items-center gap-gap-sm w-width-full rounded-border-radius-sm p-padding-sm bg-primary-nav-act";

// Links only: white box behind the icon
const linkIconBoxClass =
  "bg-text-primary p-padding-sm rounded-border-radius-sm flex-shrink-0 flex items-center justify-center";

// Files and images: no box, just the icon
const fileIconBoxClass = "flex-shrink-0 flex items-center justify-center";

const getValidFileName = (...names) =>
  names
    .find(
      (name) =>
        typeof name === "string" &&
        name.trim() !== "" &&
        name.trim().toLowerCase() !== "false" &&
        name.trim().toLowerCase() !== "true",
    )
    ?.trim();

const downloadBlob = (blob, fileName) => {
  if (!(blob instanceof Blob)) return false;

  const originalName = getValidFileName(fileName, blob.name) || "attachment";

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = objectUrl;
  link.download = originalName;

  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

  return true;
};

const getLocalBlob = (file) => {
  if (file instanceof Blob) return file;
  if (file?.file instanceof Blob) return file.file;
  return null;
};

const isFileLike = (value) => {
  if (getLocalBlob(value)) return true;

  if (!value || typeof value !== "object") return false;

  return Boolean(
    getValidFileName(
      value?.name,
      value?.metadata?.filename,
      value?.file?.name,
    ) ||
    value?.file_url ||
    value?.url,
  );
};

const valuesAsArray = (value) => {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

const getUploadedFilesForField = (field, uploadedFiles = {}) => {
  if (!uploadedFiles || typeof uploadedFiles !== "object") return [];

  const fieldName = String(field?.name || "");
  if (!fieldName) return [];

  const exactKeys = [
    fieldName,
    `${fieldName}_${field?.data_type || ""}`,
    `${fieldName}_upload`,
    `${fieldName}_file`,
    `${fieldName}_image`,
  ];

  for (const key of exactKeys) {
    const matches = valuesAsArray(uploadedFiles[key]).filter(isFileLike);
    if (matches.length) return matches;
  }

  const normalizeKey = (key) => key.toLowerCase().replace(/[^a-z0-9]/g, "");
  const normalizedFieldName = normalizeKey(fieldName);

  const matchingEntry = Object.entries(uploadedFiles).find(
    ([key, value]) =>
      normalizeKey(key).includes(normalizedFieldName) &&
      valuesAsArray(value).some(isFileLike),
  );

  if (matchingEntry) {
    return valuesAsArray(matchingEntry[1]).filter(isFileLike);
  }

  // No fallback: a field only shows files that belong to it
  return [];
};

const AdditionalDetailsPreviewMapper = ({
  mappingData,
  previewImages,
  uploadedFiles = {},
  fileOnClick,
}) => {
  const safeMapping = Array.isArray(mappingData) ? mappingData : [];
  const safePreviewImages = previewImages || {};

  const [openImageUrl, setOpenImageUrl] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});
  const [previewObjectUrl, setPreviewObjectUrl] = useState("");

  const convertToMonthYear = (input) => {
    if (!input || typeof input !== "string") {
      return globalText.NOT_GIVEN;
    }

    const [month, year] = input.split(/[-,/ ]+/);
    const monthName = MONTHS[parseInt(month, 10) - 1];

    return monthName && year ? `${monthName}, ${year}` : globalText.NOT_GIVEN;
  };

  const getFieldLabel = (field) =>
    `${field?.name || ""}${field?.mandatory === "Y" ? "" : " (optional)"}`;

  const hasFieldValue = (field) => {
    const { value, data_type: dataType } = field || {};

    if (
      value === undefined ||
      value === null ||
      value === "" ||
      value === false ||
      value === globalText.NOT_GIVEN
    ) {
      return false;
    }

    if (dataType === "multi_select_list") {
      return Array.isArray(value) && value.length > 0;
    }

    return true;
  };

  const getFieldValue = (field) => {
    const { value, data_type: dataType } = field || {};

    switch (dataType) {
      case "time":
        return value || globalText.NOT_GIVEN;

      case "from_year_to_year":
        return value?.from !== undefined &&
          value?.from !== null &&
          value?.from !== "" &&
          value?.to !== undefined &&
          value?.to !== null &&
          value?.to !== ""
          ? `${value.from} - ${value.to}`
          : globalText.NOT_GIVEN;

      case "month_year":
        return convertToMonthYear(value);

      case "multi_select_list":
        return Array.isArray(value) && value.length > 0
          ? value.join(", ")
          : globalText.NOT_GIVEN;

      case "date_range":
        return value?.start_date && value?.end_date
          ? `${value.start_date} - ${value.end_date}`
          : globalText.NOT_GIVEN;

      case "toggle":
        return value === true
          ? "Yes"
          : value === false
            ? "No"
            : globalText.NOT_GIVEN;

      default: {
        const isEmptyArray = Array.isArray(value) && value.length === 0;

        const isEmptyObject =
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value) &&
          Object.keys(value).length === 0;

        if (
          value === "" ||
          value === null ||
          value === undefined ||
          value === false ||
          isEmptyArray ||
          isEmptyObject
        ) {
          return globalText.NOT_GIVEN;
        }

        return value;
      }
    }
  };

  const getFieldLabelText = (field) => {
    if (
      typeof field?.value === "object" &&
      field?.value !== null &&
      field?.data_type === "from_year_to_year"
    ) {
      return `From year - To year${
        field?.mandatory === "Y" ? "" : " (optional)"
      }`;
    }

    return getFieldLabel(field);
  };

  const isUploadField = (field) =>
    /upload|file|image/i.test(field?.data_type || "");

  const isAttachment = (field) =>
    String(field?.data_type || "").toLowerCase() === "hyperlink" ||
    isUploadField(field);

  const getFiles = (field) => valuesAsArray(field?.value).filter(isFileLike);

  const getFilesForField = (field) => {
    const uploadedForField = getUploadedFilesForField(field, uploadedFiles);

    if (uploadedForField.length) return uploadedForField;

    return getFiles(field);
  };

  const hasAttachmentValue = (field) => {
    if (String(field?.data_type || "").toLowerCase() === "hyperlink") {
      return hasFieldValue(field);
    }

    return getFilesForField(field).length > 0;
  };

  const normalizeUrl = (url) =>
    /^https?:\/\//i.test(url) ? url : `https://${url}`;

  const formatSize = (size) => {
    const numericSize = typeof size === "number" ? size : Number(size);

    if (!Number.isFinite(numericSize) || numericSize <= 0) {
      return "";
    }

    return numericSize >= 1024 * 1024
      ? `${(numericSize / (1024 * 1024)).toFixed(1)}MB`
      : `${Math.max(1, Math.round(numericSize / 1024))}KB`;
  };

  const detailFields = safeMapping.filter((field) => !isAttachment(field));

  const attachmentFields = safeMapping.filter(
    (field) =>
      isAttachment(field) &&
      (SHOW_EMPTY_ATTACHMENTS || hasAttachmentValue(field)),
  );

  const renderNotGiven = () => (
    <p className="text-font-sm text-text-primary">{globalText.NOT_GIVEN}</p>
  );

  const renderAttachment = (field) => {
    if (String(field?.data_type || "").toLowerCase() === "hyperlink") {
      if (!hasFieldValue(field)) return renderNotGiven();

      return (
        <a
          href={normalizeUrl(String(field.value))}
          target="_blank"
          rel="noopener noreferrer"
          className={`${tileClass} cursor-pointer`}
        >
          <div className={linkIconBoxClass}>
            <SVGComponent
              url={ICONS?.LINK_WHITE}
              height={"var(--icon-sm)"}
              width={"var(--icon-sm)"}
              color={"var(--text-primary)"}
            />
          </div>

          <p className="text-font-xs text-text-primary break-all min-w-0">
            {linkIdentifier(field.value)}
          </p>
        </a>
      );
    }

    const files = getFilesForField(field);

    if (!files.length) return renderNotGiven();

    return (
      <div className="flex flex-col gap-gap-sm">
        {files.map((file, index) => {
          const localBlob = getLocalBlob(file);

          const fileName =
            getValidFileName(
              localBlob?.name,
              file?.metadata?.filename,
              file?.name,
              file?.file?.name,
            ) || "attachment";

          const fileExtension = fileName.includes(".")
            ? fileName.split(".").pop().toLowerCase()
            : "";

          const mimeType =
            localBlob?.type || file?.type || file?.metadata?.mime_type || "";

          const fileType = mimeType.split("/").pop().toUpperCase();

          const isImage =
            mimeType.startsWith("image/") ||
            IMAGE_TYPES.includes(fileType) ||
            IMAGE_EXTENSIONS.includes(fileExtension);

          const previewImage = isImage
            ? safePreviewImages[`${field?.name}_${field?.data_type}`]
            : null;

          const remoteUrl = file?.file_url || file?.url;

          const iconSrc =
            (isImage &&
              (remoteUrl ||
                (typeof previewImage === "string" && previewImage))) ||
            FileFormatMapper(fileExtension, "confirm") ||
            ICONS?.DEFAULT_FILE;

          const handleOpen = async () => {
            // Images open in the preview popup, not as downloads.
            if (isImage) {
              let imageUrl = remoteUrl;

              if (!imageUrl && localBlob) {
                if (previewObjectUrl) {
                  URL.revokeObjectURL(previewObjectUrl);
                }

                imageUrl = URL.createObjectURL(localBlob);
                setPreviewObjectUrl(imageUrl);
              }

              if (
                !imageUrl &&
                typeof previewImage === "string" &&
                previewImage
              ) {
                imageUrl = previewImage;
              }

              if (imageUrl) {
                setSelectedImage({
                  name: fileName,
                  url: imageUrl,
                });

                setOpenImageUrl(true);
                return;
              }

              fileOnClick?.({
                name: remoteUrl || `${field?.name}_${field?.data_type}`,
                type: remoteUrl ? "image-url" : "image",
              });

              return;
            }

            // Non-image local files download with the original filename.
            if (localBlob && downloadBlob(localBlob, fileName)) {
              return;
            }

            // Non-image server files download when a URL is available.
            if (remoteUrl) {
              try {
                const response = await fetch(remoteUrl);

                if (!response.ok) {
                  throw new Error(`Download failed: ${response.status}`);
                }

                const downloadedBlob = await response.blob();

                downloadBlob(downloadedBlob, fileName);
              } catch (error) {
                console.error("Unable to download attachment:", error);

                window.open(remoteUrl, "_blank", "noopener,noreferrer");
              }

              return;
            }

            fileOnClick?.({
              name: `${field?.name}_${field?.data_type}`,
              type: "file",
            });
          };

          const meta = [
            formatSize(localBlob?.size ?? file?.size),
            fileExtension.toUpperCase(),
          ]
            .filter(Boolean)
            .join(" . ");

          return (
            <div
              key={`${fileName}_${index}`}
              role="button"
              tabIndex={0}
              onClick={handleOpen}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleOpen();
                }
              }}
              className={`${tileClass} cursor-pointer`}
            >
              <div className={fileIconBoxClass}>
                <img
                  src={iconSrc}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = ICONS?.DEFAULT_FILE;
                  }}
                  alt="file"
                  className="w-icon-lg h-icon-lg shrink-0 object-contain rounded-border-radius-sm"
                />
              </div>

              <div className="flex flex-col min-w-0">
                <p className="text-font-sm text-text-primary break-all">
                  {fileName}
                </p>

                {meta && (
                  <p className="text-font-xs text-text-secondary">{meta}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const closeImagePreview = () => {
    setOpenImageUrl(false);
    setSelectedImage({});

    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl("");
    }
  };

  return (
    <div className="w-width-full flex flex-col gap-gap-md">
      {(safeMapping.length === 0 || detailFields.length > 0) && (
        <div className="bg-primary-card rounded-border-radius-md p-padding-md w-width-full flex flex-col gap-gap-md">
          <span className="text-font-md font-semibold text-text-primary">
            {globalTextFormat(globalText.CATEGORY_SPECIFIC_DETAILS, [0, 1, 2])}
          </span>

          <div className="w-width-full flex flex-col">
            {safeMapping.length === 0 ? (
              <p className="text-left text-text-primary text-sm">
                {globalText.ADDITIONAL_DETAILS_UNSUPPORTED}
              </p>
            ) : (
              detailFields.map((field, index) => (
                <div key={`${field?.name}_${field?.data_type}_${index}`}>
                  <div className="w-width-full flex flex-col gap-gap-xs">
                    <p className="text-font-sm text-text-primary overflow-hidden whitespace-pre-line break-all">
                      {linkIdentifier(getFieldValue(field))}
                    </p>

                    <p className="text-font-xs text-text-secondary">
                      {getFieldLabelText(field)}
                    </p>
                  </div>

                  {index !== detailFields.length - 1 && (
                    <div className="border-b border-primary-bg my-margin-sm" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {attachmentFields.length > 0 && (
        <div className="bg-primary-card rounded-border-radius-md p-padding-md w-width-full flex flex-col gap-gap-md">
          <span className="text-font-md font-semibold text-text-primary">
            Attachments
          </span>

          <div className="w-width-full flex flex-col">
            {attachmentFields.map((field, index) => {
              const isEmpty = !hasAttachmentValue(field);

              return (
                <div key={`${field?.name}_${field?.data_type}_${index}`}>
                  {isEmpty ? (
                    // Empty: value first, label below (same as the normal fields)
                    <div className="w-width-full flex flex-col gap-gap-xs">
                      <p className="text-font-sm text-text-primary">
                        {globalText.NOT_GIVEN}
                      </p>

                      <p className="text-font-xs text-text-secondary">
                        {getFieldLabel(field)}
                      </p>
                    </div>
                  ) : (
                    // Has data: label on top, tile below (unchanged)
                    <div className="w-width-full flex flex-col gap-gap-sm">
                      <p className="text-font-xs text-text-primary">
                        {getFieldLabel(field)}
                      </p>

                      {renderAttachment(field)}
                    </div>
                  )}

                  {index !== attachmentFields.length - 1 && (
                    <div className="border-b border-primary-bg my-margin-sm" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {openImageUrl && (
        <div className="fixed top-top-base left-left-base w-width-full h-height-full flex items-center bg-PopUp-BackDrop justify-center z-z-index-modal bg-opacity-dark-opacity">
          <MediumImagePopup
            image={selectedImage}
            closehandleFunction={closeImagePreview}
          />
        </div>
      )}
    </div>
  );
};

export default AdditionalDetailsPreviewMapper;
